/**
 * narrativeApi — REST client for the story-level and canvas-item (chart/kpi)
 * narrative + summary EDIT endpoints (generate / improve / manual override).
 *
 * Toggle via the shared REACT_APP_STORY_CANVAS_API flag:
 *   unset / "live" → real HTTP calls
 *   "mock"         → offline fixtures from narrativeApi.mock.ts
 *
 * Which family to call is a caller decision (the *type of item* being edited):
 *   • story-level functions address the canvas's singleton narrative widget
 *   • canvas-item functions address a chart/kpi's attached narrative by itemId
 */
import { isMockApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService } from "../api";
import type { CanvasAuthResponse } from "../storyCanvas/storyCanvas.types";
import type {
  CanvasItemNarrativeManualUpdateRequest,
  CanvasItemNarrativeResponse,
  CanvasItemSummaryAcceptedResponse,
  CanvasItemSummaryManualUpdateRequest,
  CanvasItemSummaryResponse,
  NarrativeImproveRequest,
  StoryNarrativeManualUpdateRequest,
  StoryNarrativeResponse,
  StorySummaryAcceptedResponse,
  StorySummaryManualUpdateRequest,
  StorySummaryResponse,
} from "./narrativeApi.types";
import {
  mockDeleteCanvasItemNarrative,
  mockGenerateCanvasItemNarrative,
  mockGenerateCanvasItemSummary,
  mockGenerateStoryNarrative,
  mockGenerateStorySummary,
  mockImproveCanvasItemNarrative,
  mockImproveCanvasItemSummary,
  mockImproveStoryNarrative,
  mockImproveStorySummary,
  mockUpdateCanvasItemNarrative,
  mockUpdateCanvasItemSummary,
  mockUpdateStoryNarrative,
  mockUpdateStorySummary,
} from "./narrativeApi.mock";

const isMock = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_CANVAS_API);

// ─── Story-level narrative ────────────────────────────────────────────────────

export async function generateStoryNarrative(
  workspaceId: string,
  storyId: string,
): Promise<StoryNarrativeResponse> {
  if (isMock()) return mockGenerateStoryNarrative(storyId);

  const envelope = await apiService.post<CanvasAuthResponse<StoryNarrativeResponse>>(
    API_ENDPOINTS.stories.storyNarrativeGenerate(workspaceId, storyId),
    {},
  );
  return envelope.data;
}

export async function improveStoryNarrative(
  workspaceId: string,
  storyId: string,
  body?: NarrativeImproveRequest,
): Promise<StoryNarrativeResponse> {
  if (isMock()) return mockImproveStoryNarrative(storyId, body);

  const envelope = await apiService.post<CanvasAuthResponse<StoryNarrativeResponse>>(
    API_ENDPOINTS.stories.storyNarrativeImprove(workspaceId, storyId),
    body ?? {},
  );
  return envelope.data;
}

export async function updateStoryNarrative(
  workspaceId: string,
  storyId: string,
  body: StoryNarrativeManualUpdateRequest,
): Promise<StoryNarrativeResponse> {
  if (isMock()) return mockUpdateStoryNarrative(storyId, body);

  const envelope = await apiService.put<CanvasAuthResponse<StoryNarrativeResponse>>(
    API_ENDPOINTS.stories.storyNarrativeUpdate(workspaceId, storyId),
    body,
  );
  return envelope.data;
}

// ─── Canvas-item (chart/kpi) narrative ──────────────────────────────────────

export async function generateCanvasItemNarrative(
  workspaceId: string,
  storyId: string,
  itemId: string,
): Promise<CanvasItemNarrativeResponse> {
  if (isMock()) return mockGenerateCanvasItemNarrative(storyId, itemId);

  const envelope = await apiService.post<
    CanvasAuthResponse<CanvasItemNarrativeResponse>
  >(
    API_ENDPOINTS.stories.canvasItemNarrativeGenerate(
      workspaceId,
      storyId,
      itemId,
    ),
    {},
  );
  return envelope.data;
}

export async function improveCanvasItemNarrative(
  workspaceId: string,
  storyId: string,
  itemId: string,
  body?: NarrativeImproveRequest,
): Promise<CanvasItemNarrativeResponse> {
  if (isMock()) return mockImproveCanvasItemNarrative(storyId, itemId, body);

  const envelope = await apiService.post<
    CanvasAuthResponse<CanvasItemNarrativeResponse>
  >(
    API_ENDPOINTS.stories.canvasItemNarrativeImprove(
      workspaceId,
      storyId,
      itemId,
    ),
    body ?? {},
  );
  return envelope.data;
}

/**
 * Delete ONLY a chart/kpi's attached narrative/summary — the canvas item
 * itself stays. Callers typically follow up with a position patch so the
 * chart expands into the space the summary card occupied.
 */
export async function deleteCanvasItemNarrative(
  workspaceId: string,
  storyId: string,
  itemId: string,
): Promise<void> {
  if (isMock()) return mockDeleteCanvasItemNarrative(itemId);

  await apiService.delete<CanvasAuthResponse<unknown>>(
    API_ENDPOINTS.stories.canvasItemNarrativeDelete(workspaceId, storyId, itemId),
  );
}

// ─── Story-level summary ──────────────────────────────────────────────────────
// Owns ONLY the story card's summary section (.../stories/:id/summary[...]).
// Improve is async: 202 accepted stub, confirmed text lands via widget refetch.

export async function generateStorySummary(
  workspaceId: string,
  storyId: string,
): Promise<StorySummaryResponse | StorySummaryAcceptedResponse> {
  if (isMock()) return mockGenerateStorySummary(storyId);

  const envelope = await apiService.post<
    CanvasAuthResponse<StorySummaryResponse | StorySummaryAcceptedResponse>
  >(API_ENDPOINTS.stories.storySummaryGenerate(workspaceId, storyId), {});
  return envelope.data;
}

export async function improveStorySummary(
  workspaceId: string,
  storyId: string,
  body?: NarrativeImproveRequest,
): Promise<StorySummaryAcceptedResponse> {
  if (isMock()) return mockImproveStorySummary(storyId, body);

  const envelope = await apiService.post<
    CanvasAuthResponse<StorySummaryAcceptedResponse>
  >(
    API_ENDPOINTS.stories.storySummaryImprove(workspaceId, storyId),
    body ?? {},
  );
  return envelope.data;
}

export async function updateStorySummary(
  workspaceId: string,
  storyId: string,
  body: StorySummaryManualUpdateRequest,
): Promise<StorySummaryResponse> {
  if (isMock()) return mockUpdateStorySummary(storyId, body);

  const envelope = await apiService.put<CanvasAuthResponse<StorySummaryResponse>>(
    API_ENDPOINTS.stories.storySummaryUpdate(workspaceId, storyId),
    body,
  );
  return envelope.data;
}

// ─── Canvas-item (chart/kpi) summary ─────────────────────────────────────────
// Sibling family targeting the chart/kpi's attached AI-summary card. Unlike
// narrative, generate/improve can run asynchronously: the backend answers 202
// with an accepted stub and the confirmed text arrives via the widget refetch.

export async function generateCanvasItemSummary(
  workspaceId: string,
  storyId: string,
  itemId: string,
): Promise<CanvasItemSummaryResponse | CanvasItemSummaryAcceptedResponse> {
  if (isMock()) return mockGenerateCanvasItemSummary(storyId, itemId);

  const envelope = await apiService.post<
    CanvasAuthResponse<CanvasItemSummaryResponse | CanvasItemSummaryAcceptedResponse>
  >(
    API_ENDPOINTS.stories.canvasItemSummaryGenerate(workspaceId, storyId, itemId),
    {},
  );
  return envelope.data;
}

export async function improveCanvasItemSummary(
  workspaceId: string,
  storyId: string,
  itemId: string,
  body?: NarrativeImproveRequest,
): Promise<CanvasItemSummaryAcceptedResponse> {
  if (isMock()) return mockImproveCanvasItemSummary(itemId, body);

  const envelope = await apiService.post<
    CanvasAuthResponse<CanvasItemSummaryAcceptedResponse>
  >(
    API_ENDPOINTS.stories.canvasItemSummaryImprove(workspaceId, storyId, itemId),
    body ?? {},
  );
  return envelope.data;
}

export async function updateCanvasItemSummary(
  workspaceId: string,
  storyId: string,
  itemId: string,
  body: CanvasItemSummaryManualUpdateRequest,
): Promise<CanvasItemSummaryResponse> {
  if (isMock()) return mockUpdateCanvasItemSummary(storyId, itemId, body);

  const envelope = await apiService.put<
    CanvasAuthResponse<CanvasItemSummaryResponse>
  >(
    API_ENDPOINTS.stories.canvasItemSummaryUpdate(workspaceId, storyId, itemId),
    body,
  );
  return envelope.data;
}

export async function updateCanvasItemNarrative(
  workspaceId: string,
  storyId: string,
  itemId: string,
  body: CanvasItemNarrativeManualUpdateRequest,
): Promise<CanvasItemNarrativeResponse> {
  if (isMock()) return mockUpdateCanvasItemNarrative(storyId, itemId, body);

  const envelope = await apiService.put<
    CanvasAuthResponse<CanvasItemNarrativeResponse>
  >(
    API_ENDPOINTS.stories.canvasItemNarrativeUpdate(
      workspaceId,
      storyId,
      itemId,
    ),
    body,
  );
  return envelope.data;
}
